import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Page Le Fondateur — POC Phase 3
 * URL : /plateforme/fondateur
 * Histoire, crédibilité, lignage de Ralph-David Jean-Pierre.
 * Page critique pour la conversion : le visiteur doit sentir l'authenticité avant d'acheter.
 */
export default function PlateformeFondateur() {
  useEffect(() => {
    document.title = "Ralph-David Jean-Pierre — Le Fondateur · Institut Impact Élite";
    const linkPreconn = document.createElement("link");
    linkPreconn.rel = "preconnect";
    linkPreconn.href = "https://fonts.googleapis.com";
    document.head.appendChild(linkPreconn);
    const linkFonts = document.createElement("link");
    linkFonts.rel = "stylesheet";
    linkFonts.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;500;700&display=swap";
    document.head.appendChild(linkFonts);
    return () => {
      document.head.removeChild(linkPreconn);
      document.head.removeChild(linkFonts);
    };
  }, []);

  const baseStyle = {
    fontFamily: "Inter, system-ui, sans-serif",
    background: "#0d0d0d",
    color: "#f5f5f5",
    minHeight: "100vh",
  };

  // Timeline / chapitres de vie
  const TIMELINE = [
    {
      year: "2001",
      chapter: "L'éveil",
      title: "Premiers entraînements",
      desc: "Tout commence à Val-d'Or. La discipline avant tout. Le respect des anciens. La compréhension que le combat est d'abord intérieur.",
    },
    {
      year: "2008-2015",
      chapter: "Les rings",
      title: "Compétiteur",
      desc: "Combats en Kickboxing et Muay Thaï. Apprendre à perdre avant d'apprendre à gagner. Bâtir une mentalité que personne ne pourra te prendre.",
    },
    {
      year: "2015",
      chapter: "L'Institut",
      title: "Fondation Institut Impact Élite Martial",
      desc: "Ouverture du dojo à Val-d'Or. Premier objectif : transmettre ce que les rings m'ont appris. Pas une école de plus — un cadre exigeant pour ceux qui veulent vraiment évoluer.",
    },
    {
      year: "2017",
      chapter: "Le circuit",
      title: "Premier Gala Gladiateurs du Nord",
      desc: "Création du Gala Gladiateurs du Nord. Un événement signature pour donner aux athlètes de la région une plateforme professionnelle digne de leur travail.",
    },
    {
      year: "2020-2024",
      chapter: "L'expansion",
      title: "Reconnaissance internationale",
      desc: "Sanctions ISKA Québec et WKN International obtenues. Athlètes formés à l'Institut qui combattent au-delà des frontières du Québec, du Canada, du monde.",
    },
    {
      year: "2026",
      chapter: "La transmission",
      title: "Lancement Plateforme Formation",
      desc: "L'Institut s'ouvre au monde. Ce que j'ai appris en 25 ans de terrain devient accessible à quiconque veut s'engager sérieusement, peu importe où il habite.",
    },
  ];

  const VALEURS = [
    { title: "Discipline", desc: "Sans elle, rien ne tient. La régularité bat le talent qui ne travaille pas." },
    { title: "Rigueur", desc: "La technique correcte, exécutée 1000 fois. Le détail qui sépare l'amateur du sérieux." },
    { title: "Transmission", desc: "Ce qui a été reçu doit être donné. Le savoir n'est pas une propriété, c'est une responsabilité." },
    { title: "Authenticité", desc: "Pas de bullshit. Pas de raccourci. Pas de fausse promesse. Ce que tu fais est ce que tu deviens." },
  ];

  return (
    <div style={baseStyle} data-testid="fondateur-page">
      <style>{`
        .editorial { font-family: 'Playfair Display', serif; }
        .label { letter-spacing: 0.4em; font-size: 11px; }
        .frame { border: 1px solid rgba(201, 169, 97, 0.25); }
        .divider-gold { background: linear-gradient(90deg, transparent, #C9A961 30%, #C9A961 70%, transparent); height: 1px; }
        .gold { color: #C9A961; }
        .red-c { color: #C8102E; }
        .bg-red-c { background: #C8102E; }
        .bg-gold { background: #C9A961; }
        .btn-primary { background: #C8102E; color: #fff; transition: 0.3s; }
        .btn-primary:hover { background: #fff; color: #C8102E; }
        .btn-secondary { color: #C9A961; border: 1px solid #C9A961; transition: 0.3s; background: transparent; }
        .btn-secondary:hover { background: #C9A961; color: #0d0d0d; }
        .pf-link { transition: color 0.2s; }
        .pf-link:hover { color: #C9A961; }
        .timeline-marker {
          position: absolute;
          left: 0;
          top: 8px;
          width: 12px;
          height: 12px;
          background: #C9A961;
          border-radius: 50%;
        }
      `}</style>

      {/* === NAV === */}
      <nav className="px-10 py-6 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md"
        style={{ background: "rgba(0,0,0,0.8)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Link to="/plateforme" className="flex items-center gap-4" data-testid="nav-back">
          <div className="relative">
            <div className="w-12 h-12 bg-red-c flex items-center justify-center">
              <span className="editorial text-white text-2xl font-black">I</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gold"></div>
          </div>
          <div>
            <div className="label gold">— INSTITUT —</div>
            <div className="editorial text-2xl font-bold leading-none mt-0.5">Impact Élite</div>
          </div>
        </Link>
        <div className="hidden md:flex gap-6 text-sm items-center">
          <Link to="/plateforme" className="pf-link">← Retour</Link>
          <Link to="/plateforme#tarifs" className="btn-primary px-6 py-2 text-xs tracking-[0.3em] font-bold">DÉBUTER →</Link>
        </div>
      </nav>

      {/* === HERO PORTRAIT === */}
      <section className="grid md:grid-cols-12 min-h-[80vh]" data-testid="hero-fondateur">
        <div className="md:col-span-7 px-10 py-20 flex flex-col justify-center">
          <div className="label red-c mb-8">VOLUME 02 — LE FONDATEUR</div>
          <div className="text-xs tracking-[0.3em] gold mb-4">UN HOMME · UNE MÉTHODE · 25 ANS</div>
          <h1 className="editorial font-black text-6xl md:text-8xl leading-[0.9] mb-10" data-testid="fondateur-name">
            Ralph-David<br />
            <span className="italic gold">Jean-Pierre</span>
          </h1>
          <div className="divider-gold w-32 mb-10"></div>
          <p className="editorial italic text-2xl md:text-3xl text-white/80 max-w-2xl leading-relaxed">
            « Mon rôle n'est pas seulement d'enseigner des mouvements. Mon rôle est de transmettre <span className="gold not-italic font-bold">une méthode</span>, une mentalité, un cadre et une force intérieure. »
          </p>
          <div className="mt-12 grid grid-cols-3 gap-8 max-w-xl">
            <div><div className="editorial text-4xl font-bold gold">25+</div><div className="label text-white/50 mt-1">ANS</div></div>
            <div><div className="editorial text-4xl font-bold gold">04</div><div className="label text-white/50 mt-1">DISCIPLINES</div></div>
            <div><div className="editorial text-4xl font-bold gold">100s</div><div className="label text-white/50 mt-1">ATHLÈTES FORMÉS</div></div>
          </div>
        </div>
        <div className="md:col-span-5 bg-red-c relative flex flex-col justify-center px-10 py-20">
          <div className="absolute top-10 right-10 label text-white/70">N°&nbsp;002</div>
          <div className="absolute bottom-10 right-10 label text-white/70">VAL-D'OR · QUÉBEC</div>
          <div className="frame p-10" style={{ borderColor: "rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.2)", backdropFilter: "blur(8px)" }}>
            <div className="label text-white/80 mb-6">— TITRES & RÔLES —</div>
            <ul className="space-y-4 text-white">
              <li className="flex gap-3"><span className="gold">◆</span><span>Coach principal · Institut Impact Élite Martial</span></li>
              <li className="flex gap-3"><span className="gold">◆</span><span>Producteur · Gala Gladiateurs du Nord</span></li>
              <li className="flex gap-3"><span className="gold">◆</span><span>Bâtisseur de circuit professionnel québécois</span></li>
              <li className="flex gap-3"><span className="gold">◆</span><span>Fondateur · Fondation Ralph-David Jean-Pierre</span></li>
              <li className="flex gap-3"><span className="gold">◆</span><span>Sanctions ISKA Québec · WKN International</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* === LE PARCOURS / TIMELINE === */}
      <section className="px-10 py-24" style={{ background: "#161616" }} data-testid="timeline-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <div className="label red-c mb-4">— LE PARCOURS —</div>
            <h2 className="editorial font-black text-5xl md:text-6xl mb-4">Vingt-cinq ans, en six chapitres.</h2>
            <p className="text-white/60 max-w-2xl">L'évolution n'est jamais linéaire. Mais chaque étape m'a appris quelque chose qui se transmet aujourd'hui.</p>
          </div>
          <div className="space-y-16 relative">
            <div className="absolute left-[5px] top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, transparent, #C9A961 20%, #C9A961 80%, transparent)" }}></div>
            {TIMELINE.map((t, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-8 pl-10 relative" data-testid={`timeline-${i}`}>
                <div className="timeline-marker"></div>
                <div className="md:col-span-3">
                  <div className="editorial font-black text-4xl gold">{t.year}</div>
                  <div className="label text-white/40 mt-2">{t.chapter}</div>
                </div>
                <div className="md:col-span-9">
                  <div className="editorial font-bold text-3xl mb-3">{t.title}</div>
                  <div className="text-white/75 leading-relaxed">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === MA MÉTHODE / LES VALEURS === */}
      <section className="px-10 py-24" data-testid="valeurs-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="label red-c mb-4">— LES PILIERS —</div>
            <h2 className="editorial font-black text-5xl md:text-6xl mb-4">Quatre valeurs. Aucun compromis.</h2>
            <p className="text-white/60 max-w-2xl">Voilà ce qui me guide en tant que coach, en tant que combattant, en tant que bâtisseur. Et ce que je transmets à chaque athlète qui passe par l'Institut.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-px" style={{ background: "rgba(201,169,97,0.2)" }}>
            {VALEURS.map((v, i) => (
              <div key={i} className="p-12" style={{ background: "#0d0d0d" }} data-testid={`valeur-${i}`}>
                <div className="editorial font-black text-7xl gold mb-6" style={{ opacity: 0.6 }}>0{i + 1}</div>
                <div className="editorial font-bold text-3xl mb-3">{v.title}</div>
                <div className="text-white/75 leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === CITATION FINALE === */}
      <section className="px-10 py-24" style={{ background: "#161616" }} data-testid="quote-final">
        <div className="max-w-4xl mx-auto text-center">
          <div className="label gold mb-6">— PROMESSE FONDATRICE —</div>
          <p className="editorial text-3xl md:text-5xl italic leading-snug text-white/95">
            « Je veux bâtir une plateforme où les gens peuvent <span className="gold not-italic font-bold">apprendre</span>, se discipliner, se challenger, mesurer leur évolution et <span className="gold not-italic font-bold">ressentir concrètement qu'ils avancent</span>. »
          </p>
          <div className="divider-gold w-32 mx-auto my-10"></div>
          <div className="label text-white/40">— RDJP, INSTITUT IMPACT ÉLITE · 2026 —</div>
        </div>
      </section>

      {/* === CTA === */}
      <section className="px-10 py-24" data-testid="cta-final">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="editorial font-black text-4xl md:text-6xl mb-6">
            Tu veux <span className="italic gold">apprendre</span> avec moi?
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-12">
            Choisis le cycle qui te ressemble. Démarre avec PCO si tu veux découvrir, ou avec Master Club si tu veux que je t'accompagne directement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/plateforme#tarifs" className="btn-primary px-10 py-4 font-bold tracking-[0.2em] text-xs">
              VOIR LES CYCLES & TARIFS →
            </Link>
            <Link to="/plateforme" className="btn-secondary px-10 py-4 font-bold tracking-[0.2em] text-xs">
              EXPLORER LES DISCIPLINES
            </Link>
          </div>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="px-10 py-12" style={{ borderTop: "1px solid rgba(201,169,97,0.2)" }}>
        <div className="max-w-6xl mx-auto text-center">
          <div className="editorial font-black text-2xl mb-2">Institut Impact Élite</div>
          <div className="text-sm text-white/60 mb-6">Val-d'Or, Québec, Canada · sdm.rdjp@gmail.com</div>
          <div className="divider-gold w-32 mx-auto mb-4"></div>
          <div className="text-xs text-white/40 tracking-widest">© 2026 — POC PHASE 3 · LE FONDATEUR</div>
        </div>
      </footer>
    </div>
  );
}
